import React, { useState, useEffect } from 'react'
import { Box, Flex, Select, Group, Button, Avatar, Text, Loader } from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { getListUsers } from '../../../services/users'
import { IconCheck } from '@tabler/icons-react'

const defaultParams = {
  'page[num]': 1,
  'page[limit]': 10,
  'filter[quote_type][is]': null,
  'filter[statuses.id][is]': null,
  'filter[user_id][is]': null,
  'filter[agent_id][is]': null,
}

const defaultQuoteType = [
  {
    label: 'Sourcing Quote',
    value: 'sourcing'
  },
  {
    label: 'Procurement Quote',
    value: 'procurement'
  },
]
const defaultStatuses = [
  {
    label: 'Finished',
    value: '60'
  },
  {
    label: 'Waiting Quote',
    value: '61'
  },
  {
    label: 'Quote Ready',
    value: '62'
  },
  {
    label: 'Cancelled',
    value: '63'
  },
]

const FormFilterQuotes = ({ paramsQuotes, applyFilter, onClose }) => {
  const [parameter, setParameter] = useState(defaultParams)

  const [userList, setUserList] = useState([])
  const [userAgentList, setUserAgentList] = useState([])
  const [searchUser, setSearchUser] = useState('')
  const [searchUserAgent, setSearchUserAgent] = useState('')

  const [loadingUser, setLoadingUser] = useState(true)
  const [loadingUserAgent, setLoadingUserAgent] = useState(true)


  const handleGetListUsers = useDebouncedCallback(async (userId) => {
    setLoadingUser(true)
    const parameter = {
      'filter[id][is]': userId,
      'filter[name][like]': searchUser,
      'fields[user]': 'id,name,email,active',
      'page[num]': 1,
      'page[limit]': 10,
      'sort': '-created_at',
      'active': true
    }

    try {
      const response = await getListUsers(parameter)
      const dataUser = response.data.data
      const remapDataUser = dataUser.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`,
          email: val.attributes.email
        }
      })
      setUserList(remapDataUser)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingUser(false)
    }
  }, 300)

  const handleGetListUserAgents = useDebouncedCallback(async (userId) => {
    setLoadingUserAgent(true)
    const parameter = {
      'filter[id][is]': userId,
      'filter[name][like]': searchUserAgent,
      'fields[user]': 'id,name,email,active',
      'page[num]': 1,
      'page[limit]': 10,
      'sort': '-created_at',
      'active': true
    }

    try {
      const response = await getListUsers(parameter)
      const dataUser = response.data.data
      const remapDataUser = dataUser.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`,
          email: val.attributes.email
        }
      })
      setUserAgentList(remapDataUser)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingUserAgent(false)
    }
  }, 300)

  useEffect(() => {
    handleGetListUsers()
    // eslint-disable-next-line
  }, [searchUser])

  useEffect(() => {
    handleGetListUserAgents()
    // eslint-disable-next-line
  }, [searchUserAgent])

  useEffect(() => {
    if (paramsQuotes !== null) {
      handleSetParams(paramsQuotes)

      if (paramsQuotes['filter[user_id][is]']) {
        handleGetListUsers(paramsQuotes['filter[user_id][is]'])
      }
      if (paramsQuotes['filter[agent_id][is]']) {
        handleGetListUserAgents(paramsQuotes['filter[agent_id][is]'])
      }
    }
    // eslint-disable-next-line
  }, [paramsQuotes])


  const handleSetParams = (val) => {
    const prevParams = {
      'filter[quote_type][is]': val['filter[quote_type][is]'],
      'filter[statuses.id][is]': val[`filter[statuses.id][is]`],
      'filter[user_id][is]': val['filter[user_id][is]'],
      'filter[agent_id][is]': val['filter[agent_id][is]'],
    }
    setParameter(prevParams)
  }

  const handleChangeFilter = (name, val) => {
    setParameter((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const handleApplyFilter = (params) => {
    const valueParams = {
      ...params,
      'page[num]': 1,
      'page[limit]': 15,
    }
    applyFilter(valueParams)
    onClose()
  }

  const loadingDataTemplate = () => {
    return (
      <Box ta='center' my={12}>
        <Loader size={16} />
        <Text c='gray.6' fz={11}>Loading Data</Text>
      </Box>
    )
  }

  const renderUserOption = ({ option, checked }) => {
    return (
      <Group flex="1" align='center' gap="xs">
        <Flex gap='sm' align='center'>
          <Avatar variant='filled' name={option.label} radius="xl" size="sm" color='red' />
          <Box>
            <Text fz={12} fw='600' lineClamp={2}>{option.label}</Text>
            <Text fz={10} fw='500' c='gray.6' lineClamp={2}>{option.email}</Text>
          </Box>
        </Flex>
        {checked && <IconCheck style={{ marginInlineStart: 'auto' }} />}
      </Group>
    )
  }

  const mappingFormFilter = () => {
    return (
      <Box>
        <Box mb='md'>
          <Select
            name='filter[quote_type][is]'
            value={parameter['filter[quote_type][is]']}
            label='Quote Type'
            placeholder="Please Select Type"
            data={defaultQuoteType}
            onChange={(val) => handleChangeFilter('filter[quote_type][is]', val)}
            size='xs'
            clearable
          />
        </Box>

        <Box my='md'>
          <Select
            name='filter[user_id][is]'
            value={parameter['filter[user_id][is]']}
            label='User'
            placeholder="Choose User"
            data={userList}
            renderOption={renderUserOption}
            onChange={(val) => handleChangeFilter('filter[user_id][is]', val)}
            size='xs'
            searchable
            searchValue={searchUser}
            onSearchChange={setSearchUser}
            nothingFoundMessage={!loadingUser ? userList.length > 0 ? '' : 'No User Found' : loadingDataTemplate()}
            clearable
          />
        </Box>

        <Box my='md'>
          <Select
            name='filter[agent_id][is]'
            value={parameter['filter[agent_id][is]']}
            label='Agent'
            placeholder="Choose Agent"
            data={userAgentList}
            renderOption={renderUserOption}
            onChange={(val) => handleChangeFilter('filter[agent_id][is]', val)}
            size='xs'
            searchable
            searchValue={searchUserAgent}
            onSearchChange={setSearchUserAgent}
            nothingFoundMessage={!loadingUserAgent ? userAgentList.length > 0 ? '' : 'No User Found' : loadingDataTemplate()}
            clearable
          />
        </Box>

        <Box mb='md'>
          <Select
            name='filter[statuses.id][is]'
            value={parameter['filter[statuses.id][is]']}
            label='Status'
            placeholder="Please Select Status"
            data={defaultStatuses}
            onChange={(val) => handleChangeFilter('filter[statuses.id][is]', val)}
            size='xs'
            clearable
          />
        </Box>
        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onClose}>Close</Button>
              <Button size='xs' variant="filled" onClick={() => handleApplyFilter(parameter)}>Apply Filter</Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
  }
  return (
    <Box>
      {mappingFormFilter()}
    </Box>
  )
}

export default FormFilterQuotes